<template>
  <div>
    <StepContent
      width="sm"
      column
    >
      <StepIcon
        :img-src="stepIcon"
      >
        Upload a photo of your most recent paystub.
      </StepIcon>

      <!-- Step Content -->
      <AppFileInput
        v-model="file"
        :error="error"
        :upload-url="documentsUrl"
        document-type="Proof of Income"
        request="POST"
        @input="error = undefined"
        @error="handleError($event)"
        @upload-start="uploadStart = true"
        @upload-success="handleUploadComplete()"
        icon="camera"
      >
        Take a Photo of Your Paystub
      </AppFileInput>
    </StepContent>

    <StepFooter>
      <AppButtonContinue
        @click="handleContinue()"
      >
        Continue
      </AppButtonContinue>

      <StepSkip
        text="I don’t have my paystub"
        @skip="handleSkipPopup"
      />

    </StepFooter>

    <AppPopup v-if="showSkipPopup">
      <div class="skip-popup">
        <div class="skip-popup__text">
          We only want to show you options you can afford.
          Confirm your income now or we can confirm it later.
        </div>
        <AppButtonContinue
          v-data-layer
          :data-layer="datalayerSkipReturn"
          class="skip-popup__button"
          @click="showSkipPopup = false"
        >
          Provide Paystub
        </AppButtonContinue>
        <StepSkip
          text="I will provide my paystub later"
          @skip="handleSkip()"
        />
      </div>
    </AppPopup>
  </div>
</template>

<script>
import AppButtonContinue from '@/components/common/buttons/AppButtonContinue'
import AppPopup from '@/components/common/popups/AppPopup'
import AppFileInput from '@/components/common/inputs/AppFileInput'
import DataLayer from '@/data-layer'
import StepContent from '@/components/common/step/StepContent'
import StepFooter from '@/components/common/step/StepFooter'
import StepHeader from '@/components/common/step/StepHeader'
import StepIcon from '@/components/common/step/StepIcon'
import StepSkip from '@/components/common/step/StepSkip'
import submit from '@/mixins/submit'
import postAppProgress from '@/mixins/post-app-progress'
import flinks from '@/mixins/flinks'

export default {
  // Name
  name: 'upload-pay-stub',

  // Components
  components: {
    AppPopup,
    AppButtonContinue,
    AppFileInput,
    StepContent,
    StepFooter,
    StepHeader,
    StepIcon,
    StepSkip
  },

  // Mixins
  mixins: [
    submit,
    postAppProgress,
    flinks
  ],

  // Data
  data () {
    return {
      stepIcon: require('@/assets/svg/documents/paystub.svg'),
      error: undefined,
      file: undefined,
      uploadComplete: false,
      uploadStart: false,
      uploadError: undefined,
      documentsUrl: `${process.env.VUE_APP_LEAD_API_URL}/${process.env.VUE_APP_DOCUMENTS_URL || 'documents'}`,
      dataLayerStepName: 'verifyincome',
      showSkipPopup: false,
      attemptedSkip: false
    }
  },

  // Computed
  computed: {
    stepIsValid () {
      return !this.error && this.uploadComplete
    },

    datalayerStart () {
      return `${this.dataLayerStepName}_start`
    },

    datalayerComplete () {
      return `${this.dataLayerStepName}_complete`
    },

    datalayerUpload () {
      return `${this.dataLayerStepName}_upload`
    },

    datalayerSkip () {
      return `${this.dataLayerStepName}_skip`
    },

    datalayerSkipStart () {
      return `${this.datalayerSkip}_start`
    },

    datalayerSkipReturn () {
      return `${this.datalayerSkip}_return`
    },

    datalayerSkipComplete () {
      return `${this.datalayerSkip}_complete`
    }
  },

  // Mounted
  mounted () {
    DataLayer.pushEvent('event', this.datalayerStart)
  },

  // Methods
  methods: {
    /**
     * Handle Upload Complete
     * Hide any errors, update the store, and set a cookie
     */
    handleUploadComplete () {
      this.uploadStart = false
      this.uploadComplete = true
      this.error = undefined
      DataLayer.pushEvent('event', this.datalayerUpload)
    },

    /**
     * Handle Error
     * Set the error message, reset uploading states
     */
    handleError (error) {
      this.error = error
      this.uploadStart = false
      this.uploadComplete = false
    },

    /**
     * Handle Continue
     * @emits next
     */
    handleContinue () {
      if (this.submittedDocuments) {
        this.handleNext()
        return
      }
      if (this.uploadComplete) {
        DataLayer.pushEvent('event', this.datalayerComplete)
        this.handleSubmit()
      } else if (this.uploadStart) {
        this.error = 'Please wait, your file is still uploading'
      } else if (!this.file) {
        this.error = 'Please upload your paystub'
      }
    },

    async handleSubmit () {
      try {
        if (this.submittedDocuments) {
          this.handleNext()
          return
        }
        // await this.instantLeadSubmission()
        this.$store.updateApplication('submitted_documents', true)
        this.handleNext()
      } catch (error) {
        console.warn(error)
      }
    },

    async handleNext () {
      const postAppComplete = this.submittedApp && this.submittedThanks && this.submittedDocuments && !this.shouldShowBanking
      const shouldShowBanking = this.submittedApp && this.submittedThanks && this.submittedDocuments && this.shouldShowBanking

      if (postAppComplete || !this.$_flinks_isEnabled) {
        await this.instantLeadSubmission()

        return this.$router.replace({
          path: '/finished',
          query: { ...this.$route.query }
        })
      }

      if (shouldShowBanking) {
        return this.$router.push({
          path: '/banking',
          query: { ...this.$route.query }
        })
      }
    },

    handleSkip () {
      this.$nextTick(() => {
        DataLayer.pushEvent('event', this.datalayerSkipComplete)
        this.handleSubmit()
      })
    },

    handleSkipPopup () {
      DataLayer.pushEvent('event', this.datalayerSkipStart)
      if (!this.attemptedSkip && !this.uploadComplete) {
        this.attemptedSkip = true
        this.showSkipPopup = true
      } else {
        this.handleSkip()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.app-file-input {
  padding-bottom: 0;

  @include breakpoint('desktop-up') {
    padding-bottom: 30px;
  }
}

.skip-popup {
  padding: 50px 20px 0 20px;

  @include breakpoint('tablet-portrait-up') {
    padding: 70px 60px 0 60px;
  }

  &__text {
    margin-bottom: 60px;
    font-size: 14px;

    @include breakpoint('tablet-portrait-up') {
      font-size: 20px;
    }
  }

  &__button {
    width: 100%;
  }
}
</style>
